import { useCallback } from 'react';

import { useToggleLikeMutation } from '../store/services/likes';
import { useWithAuth } from './useWithAuth';
import { usePrompt } from './usePrompt';

export type UseLikeParams = {
    promptId: number;
    initialLikes: number;
    initialIsLiked: boolean;
};

export type UseLikeReturn = {
    likes: number;
    isLiked: boolean;
    toggleLike: () => void;
    isLoading?: boolean;
};

/**
 * Хук для управления лайками через RTK Query
 * Автоматически синхронизирует состояние с кешем RTK Query
 */
export const useLike = ({ promptId, initialLikes, initialIsLiked }: UseLikeParams): UseLikeReturn => {
    const { withAuth } = useWithAuth();
    const [toggleLikeMutation, { isLoading }] = useToggleLikeMutation();

    // Получаем кешированный промпт из slice
    const { data: prompt } = usePrompt(promptId);

    // Используем данные из кеша, если есть, иначе initial значения
    const currentLikes = prompt?.likes ?? initialLikes;
    const currentIsLiked = prompt?.isLiked ?? initialIsLiked;

    const toggleLike = useCallback(async () => {
        if (isLoading) {
            return;
        }

        const newIsLiked = !currentIsLiked;

        await toggleLikeMutation({
            promptId,
            isLiked: newIsLiked,
        }).unwrap();
    }, [toggleLikeMutation, promptId, currentIsLiked, isLoading]);

    const toggleLikeWithAuth = useCallback(() => {
        withAuth(toggleLike, {
            retpath: `https://${window.location.hostname}/prompthub/prompts/${promptId}`,
        });
    }, [withAuth, toggleLike, promptId]);

    return {
        likes: currentLikes,
        isLiked: currentIsLiked,
        toggleLike: toggleLikeWithAuth,
        isLoading,
    };
};
